<script>
    import { onMount } from 'svelte';
    
    // Component state
    let loading = false;
    let loadingKind = '';
    let error = '';
    let transactionCount = 0;
    let lastFetched = null;
    let paymentCount = 0;
    let lastPaymentsFetched = null;
    
    // Month selection state
    let selectedMonth = '';
    let availableMonths = [];
    
    // Generate available months from September 2024 to December 2026
    function generateAvailableMonths() {
        const months = [];
        const startDate = new Date(2024, 8); // September 2024
        const endDate = new Date(2026, 11); // December 2026
        
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const monthName = currentDate.toLocaleDateString('lt-LT', { month: 'long', year: 'numeric' });
            const monthValue = `${year}-${String(month + 1).padStart(2, '0')}`;
            
            months.push({
                value: monthValue,
                label: monthName
            });
            
            currentDate.setMonth(currentDate.getMonth() + 1);
        }
        
        return months;
    }
    
    onMount(() => {
        availableMonths = generateAvailableMonths();
    });
    
    // Fetch balance transaction data and download as CSV
    async function downloadBalanceTransactionsCSV() {
        loading = true;
        loadingKind = 'balance';
        error = '';
        
        try {
            const token = localStorage.getItem('jwtToken');
            if (!token) {
                error = 'Reikalinga autentifikacija. Prisijunkite prie sistemos.';
                loading = false;
                return;
            }
            
            // Build URL with optional month parameter
            let url = '/studentplayground/api/admin/balance-transactions-csv/';
            if (selectedMonth) {
                url += `?selected_month=${selectedMonth}`;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                error = errorData.message || 'Nepavyko gauti duomenų';
                loading = false;
                loadingKind = '';
                return;
            }

            // Backend streams CSV directly. Keep UI counters via response headers.
            const totalCountHeader = response.headers.get('X-Total-Count');
            transactionCount = totalCountHeader ? Number(totalCountHeader) : 0;
            lastFetched = new Date();

            const blob = await response.blob();
            const now = new Date();
            const timestamp = now.toISOString().split('T')[0]; // YYYY-MM-DD format
            const monthSuffix = selectedMonth ? `_${selectedMonth}` : '';
            const filename = `balance_transactions_class_charges${monthSuffix}_${timestamp}.csv`;
            downloadCsvBlob(blob, filename);
            
        } catch (err) {
            console.error('Error fetching balance transactions:', err);
            error = 'Tinklo klaida. Bandykite dar kartą.';
        } finally {
            loading = false;
            loadingKind = '';
        }
    }

    // Fetch settled Payment model data and download as CSV
    async function downloadSettledPaymentsCSV() {
        loading = true;
        loadingKind = 'payments';
        error = '';
        
        try {
            const token = localStorage.getItem('jwtToken');
            if (!token) {
                error = 'Reikalinga autentifikacija. Prisijunkite prie sistemos.';
                loading = false;
                loadingKind = '';
                return;
            }
            
            // Build URL with optional month parameter
            let url = '/studentplayground/api/admin/settled-payments-csv/';
            if (selectedMonth) {
                url += `?selected_month=${selectedMonth}`;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                error = errorData.message || 'Nepavyko gauti duomenų';
                loading = false;
                loadingKind = '';
                return;
            }

            const totalCountHeader = response.headers.get('X-Total-Count');
            paymentCount = totalCountHeader ? Number(totalCountHeader) : 0;
            lastPaymentsFetched = new Date();

            const blob = await response.blob();
            const now = new Date();
            const timestamp = now.toISOString().split('T')[0]; // YYYY-MM-DD format
            const monthSuffix = selectedMonth ? `_${selectedMonth}` : '';
            const filename = `settled_payments${monthSuffix}_${timestamp}.csv`;
            downloadCsvBlob(blob, filename);
            
        } catch (err) {
            console.error('Error fetching settled payments:', err);
            error = 'Tinklo klaida. Bandykite dar kartą.';
        } finally {
            loading = false;
            loadingKind = '';
        }
    }
    
    // Trigger CSV blob download (backend generates CSV)
    function downloadCsvBlob(blob, filename) {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
</script>

<style>
    .container {
        padding: 20px;
        max-width: 800px;
        margin: 0 auto;
    }
    
    .header {
        margin-bottom: 30px;
    }
    
    h2 {
        color: #333;
        margin-bottom: 10px;
    }
    
    .description {
        color: #666;
        font-size: 14px;
        margin-bottom: 20px;
        line-height: 1.6;
    }
    
    .month-selector-section {
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
    }
    
    .month-selector-section h3 {
        color: #333;
        font-size: 18px;
        margin-bottom: 15px;
    }
    
    .month-selector-wrapper {
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
    }
    
    .month-selector-label {
        font-weight: 600;
        color: #495057;
    }
    
    .month-selector {
        padding: 8px 12px;
        border: 1px solid #ced4da;
        border-radius: 5px;
        font-size: 14px;
        min-width: 200px;
        cursor: pointer;
    }
    
    .month-selector:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
    }
    
    .clear-month-button {
        padding: 8px 16px;
        background: #6c757d;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.2s;
    }
    
    .clear-month-button:hover {
        background: #5a6268;
    }
    
    .download-section {
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 30px;
        margin-bottom: 20px;
    }

    .download-buttons {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        align-items: center;
    }
    
    .download-button {
        background: #007bff;
        color: white;
        border: none;
        border-radius: 5px;
        padding: 12px 24px;
        font-size: 16px;
        cursor: pointer;
        transition: background-color 0.2s;
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }
    
    .download-button:hover:not(:disabled) {
        background: #0056b3;
    }

    .download-button.payments {
        background: #198754; /* green */
    }

    .download-button.payments:hover:not(:disabled) {
        background: #146c43;
    }
    
    .download-button:disabled {
        background: #6c757d;
        cursor: not-allowed;
        opacity: 0.6;
    }
    
    .download-icon {
        font-size: 18px;
    }
    
    .info-box {
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
    }
    
    .info-item {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        border-bottom: 1px solid #f0f0f0;
    }
    
    .info-item:last-child {
        border-bottom: none;
    }
    
    .info-label {
        font-weight: 600;
        color: #495057;
    }
    
    .info-value {
        color: #6c757d;
    }
    
    .error-message {
        background: #f8d7da;
        border: 1px solid #f5c6cb;
        color: #721c24;
        padding: 12px;
        border-radius: 5px;
        margin-bottom: 20px;
    }
    
    .loading-spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 2px solid #ffffff;
        border-radius: 50%;
        border-top-color: transparent;
        animation: spin 0.8s linear infinite;
    }
    
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
    
    .success-message {
        background: #d4edda;
        border: 1px solid #c3e6cb;
        color: #155724;
        padding: 12px;
        border-radius: 5px;
        margin-top: 20px;
    }
    
    .csv-format-info {
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 20px;
    }
    
    .csv-format-info h3 {
        color: #333;
        font-size: 18px;
        margin-bottom: 15px;
    }
    
    .csv-format-info ul {
        list-style-type: none;
        padding-left: 0;
    }
    
    .csv-format-info li {
        padding: 8px 0;
        color: #495057;
        border-bottom: 1px solid #f0f0f0;
    }
    
    .csv-format-info li:last-child {
        border-bottom: none;
    }
    
    .csv-format-info code {
        background: #f8f9fa;
        padding: 2px 6px;
        border-radius: 3px;
        font-family: 'Courier New', monospace;
        color: #e83e8c;
    }
</style>

<div class="container">
    <div class="header">
        <h2>📊 Balance Transactions CSV Export</h2>
        <p class="description">
            Download a CSV file of all class charge balance transactions with parent names. 
            The export includes cleaned descriptions (without Event IDs) and parent full names.
        </p>
    </div>
    
    {#if error}
        <div class="error-message">
            ⚠️ {error}
        </div>
    {/if}
    
    <div class="month-selector-section">
        <h3>📅 Filter by Month (Optional)</h3>
        <div class="month-selector-wrapper">
            <label class="month-selector-label" for="month-select">Select Month:</label>
            <select 
                id="month-select"
                class="month-selector" 
                bind:value={selectedMonth}
            >
                <option value="">All Months</option>
                {#each availableMonths as month}
                    <option value={month.value}>{month.label}</option>
                {/each}
            </select>
            {#if selectedMonth}
                <button 
                    class="clear-month-button"
                    on:click={() => selectedMonth = ''}
                >
                    Clear Filter
                </button>
            {/if}
        </div>
    </div>
    
    <div class="download-section">
        <div class="download-buttons">
            <button 
                class="download-button" 
                on:click={downloadBalanceTransactionsCSV}
                disabled={loading}
            >
                {#if loading && loadingKind === 'balance'}
                    <span class="loading-spinner"></span>
                    <span>Downloading...</span>
                {:else}
                    <span class="download-icon">⬇️</span>
                    <span>Download Balance CSV</span>
                {/if}
            </button>

            <button 
                class="download-button payments" 
                on:click={downloadSettledPaymentsCSV}
                disabled={loading}
            >
                {#if loading && loadingKind === 'payments'}
                    <span class="loading-spinner"></span>
                    <span>Downloading...</span>
                {:else}
                    <span class="download-icon">⬇️</span>
                    <span>Download Settled Payments CSV</span>
                {/if}
            </button>
        </div>
    </div>
    
    {#if lastFetched}
        <div class="info-box">
            <div class="info-item">
                <span class="info-label">Last Downloaded:</span>
                <span class="info-value">{lastFetched.toLocaleString('lt-LT')}</span>
            </div>
            <div class="info-item">
                <span class="info-label">Month Filter:</span>
                <span class="info-value">{selectedMonth || 'All Months'}</span>
            </div>
            <div class="info-item">
                <span class="info-label">Total Transactions:</span>
                <span class="info-value">{transactionCount}</span>
            </div>
        </div>
        
        <div class="success-message">
            ✅ CSV file downloaded successfully with {transactionCount} transactions!
        </div>
    {/if}

    {#if lastPaymentsFetched}
        <div class="info-box">
            <div class="info-item">
                <span class="info-label">Last Payments Downloaded:</span>
                <span class="info-value">{lastPaymentsFetched.toLocaleString('lt-LT')}</span>
            </div>
            <div class="info-item">
                <span class="info-label">Month Filter:</span>
                <span class="info-value">{selectedMonth || 'All Months'}</span>
            </div>
            <div class="info-item">
                <span class="info-label">Total Settled Payments:</span>
                <span class="info-value">{paymentCount}</span>
            </div>
        </div>
        
        <div class="success-message">
            ✅ Settled payments CSV downloaded successfully with {paymentCount} payments!
        </div>
    {/if}
    
    <div class="csv-format-info">
        <h3>📋 CSV Format Details</h3>
        <ul>
            <li><strong>Description:</strong> Cleaned class charge description (e.g., "Class charge for Pauliaus Vain. Matematikos Pamoka | Tutory on 2025-10-14 20:00")</li>
            <li><strong>Amount:</strong> Transaction amount in euros (always positive)</li>
            <li><strong>Date:</strong> Actual class date extracted from description (YYYY-MM-DD format)</li>
            <li><strong>Parent Name:</strong> Full parent name (e.g., <code>Ramunė Brasienė</code>)</li>
            <li><strong>Invoice_Data:</strong> JSON array string containing all invoice information for the user (invoice_number, storage_path, invoice_url, bucket_name, invoice_month, created_at)</li>
        </ul>
    </div>
</div>

